import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { T, F, Wordmark } from "@/lib/bricks";
import VisitViewer from "@/components/VisitViewer";
import { visitFromSharePayload } from "@/lib/visitBridge";
import { partitionSharePhotos } from "@/lib/mediaKind";
import BricksLoader from "@/components/BricksLoader";

function tourEmbedSrc(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${url.startsWith("/") ? url : `/${url}`}`;
}

function photoFullSrc(ph) {
  return ph?.download_url || ph?.file_url || ph?.url || ph?.thumbnail_url || "";
}

const ZIP_PRESETS = [
  { id: "original", label: "As delivered (2400px)" },
  { id: "mls", label: "MLS (2400px)" },
  { id: "web", label: "Web (1920px)" },
];

const PRESET_HINT = {
  original: "Edited photos at 2400px. Video and plans as delivered.",
  web: "1920px long edge. Lighter files for web and social.",
  mls: "4:3 crop, 2400px long edge.",
};

export default function PropertyListingSitePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [zipPreset, setZipPreset] = useState("original");
  const [zipError, setZipError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [leadSending, setLeadSending] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadError, setLeadError] = useState(null);

  const BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${BASE}/api/public/share/${encodeURIComponent(token)}`)
      .then((r) => {
        if (!r.ok) throw new Error(r.status === 404 ? "This listing link is invalid or has expired." : "Failed to load listing.");
        return r.json();
      })
      .then((d) => { setData(d); setLoading(false); })
      .catch((err) => { setError(err.message || "Failed to load."); setLoading(false); });
  }, [token]); // eslint-disable-line

  const photos = data?.photos || [];
  const { stillPhotos, dronePhotos, reelPhotos } = partitionSharePhotos(photos);
  // Prefer an interior/HDR still for hero — not a drone/reel thumbnail.
  const heroSrc = photoFullSrc(stillPhotos[0] || photos[0]) || (!data?.floorplan_is_pdf && data?.floorplan_url) || "";
  const tourSrc = tourEmbedSrc(data?.tour_embed_url);
  const lightboxOpen = lightboxIndex != null && lightboxIndex >= 0 && lightboxIndex < photos.length;
  const activePhoto = lightboxOpen ? photos[lightboxIndex] : null;

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(() => {
    setLightboxIndex((i) => (i == null || photos.length === 0 ? i : (i - 1 + photos.length) % photos.length));
  }, [photos.length]);
  const showNext = useCallback(() => {
    setLightboxIndex((i) => (i == null || photos.length === 0 ? i : (i + 1) % photos.length));
  }, [photos.length]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxOpen, closeLightbox, showPrev, showNext]);

  const handleDownloadAll = useCallback(async () => {
    if (downloading || !token) return;
    setZipError(null);
    setDownloading(true);
    try {
      const q = `?preset=${encodeURIComponent(zipPreset || "original")}`;
      const res = await fetch(`${BASE}/api/public/share/${encodeURIComponent(token)}/media.zip${q}`);
      if (!res.ok) {
        let detail = res.status === 404 ? "No downloadable files available yet." : "Failed to prepare zip";
        try {
          const body = await res.json();
          if (typeof body?.detail === "string") detail = body.detail;
        } catch { /* keep */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("content-disposition") || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      a.download = match?.[1] || "listing-media.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setZipError(err?.message || "Failed to prepare zip");
    } finally {
      setDownloading(false);
    }
  }, [BASE, downloading, token, zipPreset]);

  const copyPageLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setLinkCopied(false);
    }
  }, []);

  async function submitLead(e) {
    e.preventDefault();
    if (leadSending || leadDone) return;
    setLeadError(null);
    const name = lead.name.trim();
    const email = lead.email.trim();
    if (!name || !email || !email.includes("@")) {
      setLeadError("Name and a valid email are required.");
      return;
    }
    setLeadSending(true);
    try {
      const res = await fetch(`${BASE}/api/public/share/${encodeURIComponent(token)}/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone: lead.phone.trim() || undefined,
          message: lead.message.trim() || undefined,
          website: lead.website || undefined,
        }),
      });
      if (!res.ok) {
        let detail = "Could not send inquiry";
        try {
          const body = await res.json();
          if (typeof body?.detail === "string") detail = body.detail;
        } catch { /* keep */ }
        throw new Error(detail);
      }
      setLeadDone(true);
    } catch (err) {
      setLeadError(err?.message || "Could not send inquiry");
    } finally {
      setLeadSending(false);
    }
  }

  const pageStyle = {
    minHeight: "100vh", background: "#F4F1EB", fontFamily: F.display,
    color: "#14110E",
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: "center", paddingTop: 120 }}>
          <BricksLoader variant="assemble" size={40} color={T.brick} style={{ display: 'block', margin: '0 auto' }} />
          <div style={{ marginTop: 16, fontSize: 14, color: "#6B6054" }}>Loading listing…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: 480, margin: "96px auto", padding: "0 8px", textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 500, letterSpacing: -0.5 }}>Listing unavailable</div>
          <div style={{ fontSize: 14, color: "#6B6054", marginTop: 8 }}>{error}</div>
        </div>
      </div>
    );
  }

  const amenityLabels = Array.isArray(data.amenity_labels)
    ? [...new Set(data.amenity_labels.map((l) => String(l || "").trim()).filter(Boolean))]
    : [];

  return (
    <div data-testid="property-listing-site" style={pageStyle}>
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "28px 16px 72px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <Wordmark size={18} />
          <span style={{ fontSize: 12, color: "#A39684" }}>
            Listing
          </span>
        </div>

        {heroSrc ? (
          <img
            src={heroSrc}
            alt={data.address || "Listing"}
            style={{ width: "100%", height: "clamp(240px, 52vw, 460px)", objectFit: "cover", borderRadius: 2, display: "block", background: "#EDE8DF" }}
          />
        ) : (
          <div style={{ width: "100%", height: 280, borderRadius: 2, background: "#EDE8DF", display: "flex", alignItems: "center", justifyContent: "center", color: "#A39684", fontSize: 14 }}>
            Photos not delivered yet
          </div>
        )}

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginTop: 28 }}>
          <div style={{ minWidth: 0, flex: "1 1 280px" }}>
            <div style={{ fontSize: "clamp(26px, 6.4vw, 34px)", fontWeight: 500, letterSpacing: -1, lineHeight: 1.15 }}>{data.address || "Listing"}</div>
            <div style={{ fontSize: 14, color: "#6B6054", marginTop: 6 }}>
              {[tourSrc && "Tour", visitFromSharePayload(data) && "Visit", stillPhotos.length > 0 && "Photos", dronePhotos.length > 0 && "Drone", reelPhotos.length > 0 && "Video", data.floorplan_url && "Floor plan"].filter(Boolean).join(" · ") || "Listing media"} · shared via bricks
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
            <button
              type="button"
              data-testid="listing-copy-link"
              onClick={copyPageLink}
              style={{
                fontFamily: F.display, fontSize: 13, padding: "6px 0", border: "none", background: "none",
                color: "#3B332A", textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: "#14111033",
                cursor: "pointer", whiteSpace: "nowrap", alignSelf: "flex-start",
              }}
            >
              {linkCopied ? "Link copied" : "Copy this link"}
            </button>
            {!data.empty && (photos.length > 0 || data.floorplan_url) && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <select
                  data-testid="listing-zip-preset"
                  value={zipPreset}
                  onChange={(e) => setZipPreset(e.target.value)}
                  disabled={downloading}
                  style={{
                    fontFamily: F.display, fontSize: 13,
                    padding: "7px 10px", borderRadius: 2,
                    border: "1px solid #14111018", background: "#EDE8DF", color: "#14110E",
                  }}
                >
                  {ZIP_PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid="listing-download-all"
                  onClick={handleDownloadAll}
                  disabled={downloading}
                  style={{
                    fontFamily: F.display, fontSize: 13, fontWeight: 500,
                    padding: "8px 14px", borderRadius: 2, border: "none",
                    background: downloading ? "#A39684" : "#14110E", color: "#F4F1EB",
                    cursor: downloading ? "default" : "pointer",
                  }}
                >
                  {downloading ? "Preparing zip…" : "Download media"}
                </button>
              </div>
            )}
            {!data.empty && (photos.length > 0 || data.floorplan_url) && (
              <div style={{ fontSize: 12, color: "#A39684", maxWidth: 280 }}>
                {PRESET_HINT[zipPreset] || PRESET_HINT.original}
              </div>
            )}
          </div>
        </div>
        {zipError && <div style={{ marginTop: 10, fontSize: 13, color: "#8C2F24" }}>{zipError}</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: 32, marginTop: 36 }}>
          {tourSrc && (
            <section>
              <div style={{ fontSize: 12, color: "#A39684", marginBottom: 10 }}>3D tour</div>
              <iframe
                src={tourSrc}
                title="3D tour"
                allow="fullscreen; xr-spatial-tracking"
                style={{ width: "100%", height: 440, border: 0, borderRadius: 2, background: "#0a0a0a", display: "block" }}
              />
            </section>
          )}


          {visitFromSharePayload(data) && (
            <section data-testid="listing-visit">
              <div style={{ fontSize: 12, color: "#A39684", marginBottom: 10 }}>Virtual visit</div>
              <VisitViewer visit={visitFromSharePayload(data)} />
            </section>
          )}

          {amenityLabels.length > 0 && (
            <section>
              <div style={{ fontSize: 12, color: "#A39684", marginBottom: 10 }}>
                Features & amenities
              </div>
              <div style={{ fontSize: 14, color: "#3B332A", lineHeight: 1.7 }}>{amenityLabels.join(" · ")}</div>
            </section>
          )}

          {[
            { key: "photos", label: "Photos", items: stillPhotos },
            { key: "drone", label: "Drone", items: dronePhotos },
            { key: "reel", label: "Video", items: reelPhotos },
          ].map((section) => (
            section.items.length > 0 ? (
              <section key={section.key} data-testid={`listing-${section.key}`}>
                <div style={{ fontSize: 12, color: "#A39684", marginBottom: 10 }}>
                  {section.label} · {section.items.length}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
                  {section.items.map((ph) => {
                    const i = photos.indexOf(ph);
                    const full = photoFullSrc(ph);
                    return (
                      <div
                        key={ph.id || `${section.key}-${i}`}
                        role="button"
                        tabIndex={0}
                        aria-label={`Open ${section.label.toLowerCase()} ${i + 1}`}
                        onClick={() => setLightboxIndex(i)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setLightboxIndex(i); } }}
                        style={{ borderRadius: 2, overflow: "hidden", cursor: "pointer", background: "#EDE8DF" }}
                      >
                        <img
                          src={ph.thumbnail_url || full}
                          alt={ph.filename || `${section.label} ${i + 1}`}
                          style={{ width: "100%", height: 150, objectFit: "cover", display: "block" }}
                        />
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null
          ))}

          {data.floorplan_url && (
            <section>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 12, color: "#A39684" }}>Floor plan</div>
                <a
                  href={data.floorplan_url}
                  download="floor-plan"
                  rel="noopener noreferrer"
                  target="_blank"
                  style={{
                    fontFamily: F.display, fontSize: 13, color: "#3B332A",
                    textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: "#14111033",
                  }}
                >
                  Download
                </a>
              </div>
              {data.floorplan_is_pdf ? (
                <object
                  data={data.floorplan_url}
                  type="application/pdf"
                  aria-label="Floor plan"
                  style={{ width: "100%", height: 520, borderRadius: 2, background: "#EDE8DF", display: "block" }}
                >
                  <a href={data.floorplan_url} target="_blank" rel="noopener noreferrer">Open the floor plan (PDF)</a>
                </object>
              ) : (
                <img
                  src={data.floorplan_url}
                  alt="Floor plan"
                  style={{ width: "100%", maxHeight: 520, objectFit: "contain", borderRadius: 2, background: "#EDE8DF", display: "block" }}
                />
              )}
            </section>
          )}

          <section data-testid="listing-inquire" style={{ paddingTop: 28, borderTop: "1px solid #14111018" }}>
            <div style={{ fontSize: 20, fontWeight: 500, letterSpacing: -0.4 }}>Inquire about this listing</div>
            <div style={{ fontSize: 13, color: "#6B6054", marginTop: 6, marginBottom: 18 }}>
              Leave your details and the listing contact will follow up.
            </div>
            {leadDone ? (
              <div style={{ fontSize: 14, color: "#14110E", fontWeight: 500 }}>Thanks. Your inquiry is on its way.</div>
            ) : (
              <form onSubmit={submitLead} style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 480 }}>
                <input
                  required
                  placeholder="Name"
                  value={lead.name}
                  onChange={(e) => setLead((s) => ({ ...s, name: e.target.value }))}
                  style={{ fontFamily: F.display, fontSize: 14, padding: "10px 12px", borderRadius: 2, border: "1px solid #14111022", background: "#F4F1EB" }}
                />
                <input
                  required
                  type="email"
                  placeholder="Email"
                  value={lead.email}
                  onChange={(e) => setLead((s) => ({ ...s, email: e.target.value }))}
                  style={{ fontFamily: F.display, fontSize: 14, padding: "10px 12px", borderRadius: 2, border: "1px solid #14111022", background: "#F4F1EB" }}
                />
                <input
                  placeholder="Phone (optional)"
                  value={lead.phone}
                  onChange={(e) => setLead((s) => ({ ...s, phone: e.target.value }))}
                  style={{ fontFamily: F.display, fontSize: 14, padding: "10px 12px", borderRadius: 2, border: "1px solid #14111022", background: "#F4F1EB" }}
                />
                <textarea
                  placeholder="Message (optional)"
                  rows={4}
                  value={lead.message}
                  onChange={(e) => setLead((s) => ({ ...s, message: e.target.value }))}
                  style={{ fontFamily: F.display, fontSize: 14, padding: "10px 12px", borderRadius: 2, border: "1px solid #14111022", background: "#F4F1EB", resize: "vertical" }}
                />
                <input
                  aria-hidden="true"
                  tabIndex={-1}
                  autoComplete="off"
                  name="website"
                  value={lead.website}
                  onChange={(e) => setLead((s) => ({ ...s, website: e.target.value }))}
                  style={{ position: "absolute", left: -9999, width: 1, height: 1, opacity: 0 }}
                />
                {leadError && <div style={{ fontSize: 13, color: "#8C2F24" }}>{leadError}</div>}
                <button
                  type="submit"
                  disabled={leadSending}
                  style={{
                    alignSelf: "flex-start",
                    fontFamily: F.display, fontSize: 13, fontWeight: 500,
                    padding: "10px 16px", borderRadius: 2, border: "none",
                    background: leadSending ? "#A39684" : "#14110E", color: "#F4F1EB",
                    cursor: leadSending ? "default" : "pointer",
                  }}
                >
                  {leadSending ? "Sending…" : "Send inquiry"}
                </button>
              </form>
            )}
          </section>
        </div>
      </div>

      {lightboxOpen && activePhoto && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeLightbox}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(20,17,14,0.88)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 'var(--bx-pad)',
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: "min(1100px, 100%)", maxHeight: "100%" }}>
            <img
              src={photoFullSrc(activePhoto)}
              alt={activePhoto.filename || `Photo ${lightboxIndex + 1}`}
              style={{ maxWidth: "100%", maxHeight: "calc(100vh - 80px)", objectFit: "contain", display: "block", borderRadius: 2 }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
